import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, AlertCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const UsageLimitBanner = ({ className = '' }) => {
  const { t } = useTranslation();
  const { user } = useAuth(); // Detectar mudanças no usuário
  const navigate = useNavigate();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsageStatus();
    
    // Refetch when page becomes visible (user returns from Stripe or another tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('📱 Page visible - refreshing usage status');
        fetchUsageStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]); // Recarregar quando usuário muda

  const fetchUsageStatus = async () => {
    try {
      const response = await api.get('/subscription/usage-status', {validateStatus: s => s === 200 || s === 401});
      if (response.status === 401) return; // Não logado, ignorar
      setUsageData(response.data);
      
      // Logs reduzidos - apenas uma vez
      const sub = response.data.subscription;
      const planName = sub.plan === 'family' ? 'PLANO FAMILIAR' : 
                      sub.plan === 'premium' ? 'PREMIUM' : 'FREE';
      
      // Log simplificado apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log(`👤 ${planName} | Status: ${sub.status}`);
      }
    } catch (error) {
      console.error('Error fetching usage status:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !usageData) return null;

  const { subscription, usage, limits } = usageData;
  
  // LÓGICA: Mostrar APENAS para usuários FREE (não Premium, não Family, não Trial)
  const isPremium = subscription.plan === 'premium' || subscription.plan === 'family';
  const isTrial = subscription.status === 'trial';
  const isCancelled = subscription.status === 'cancelled';

  // Only show for FREE users (not premium, not trial, not in grace period)
  if (isPremium || isTrial || isCancelled) {
    return null;
  }
  
  // Se plano é free mas não tem subscription plan definido, é usuário realmente Free
  if (subscription.plan !== 'free') {
    return null;
  }

  const recipesRemaining = limits.recipes_per_month - usage.recipes_this_month;
  const aiRemaining = limits.ai_generations_per_week - usage.ai_generations_this_week;
  const isNearLimit = recipesRemaining <= 1 || aiRemaining <= 1;

  return (
    <Card className={`p-4 border-2 ${isNearLimit ? 'border-red-300 bg-red-50' : 'border-blue-300 bg-blue-50'} ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className={`w-5 h-5 ${isNearLimit ? 'text-red-600' : 'text-blue-600'}`} />
            <h3 className="font-semibold text-gray-900">🆓 {t('subscription.free')}</h3>
          </div>
          
          <div className="space-y-1 text-sm text-gray-700 mb-3">
            <p>
              📝 {t('limits.recipesThisMonth')}: <strong>{usage.recipes_this_month}/{limits.recipes_per_month}</strong>
              {recipesRemaining === 0 && <span className="text-red-600 font-semibold ml-2">{t('limits.limitReached')}</span>}
            </p>
            <p>
              🤖 {t('limits.aiThisWeek')}: <strong>{usage.ai_generations_this_week}/{limits.ai_generations_per_week}</strong>
              {aiRemaining === 0 && <span className="text-red-600 font-semibold ml-2">{t('limits.limitReached')}</span>}
            </p>
          </div>
          
          <p className="text-xs text-gray-600 italic">
            💎 Assine Premium ou Familiar para desbloquear <strong>receitas ilimitadas</strong>, <strong>IA ilimitada</strong> e muito mais!
          </p>
        </div>
        
        <Button
          onClick={() => navigate('/subscription')}
          size="sm"
          className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-md whitespace-nowrap"
        >
          <Crown className="w-4 h-4 mr-2" />
          Assinar
        </Button>
      </div>
    </Card>
  );
};

export default UsageLimitBanner;
